import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card } from 'react-bootstrap';
import PropertyCard from 'components/common/PropertyCard';
import MLSChatbot from 'components/mls-chatbot/MLSChatbot';
import './PropertyChat.css';

interface LocationState {
  initialQuery?: string;
}

const PropertyChat: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as LocationState;
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Transform API property data to match PropertyCard format
  const transformProperty = (property: any) => {
    return {
      id: property.id,
      mls_number: property.mlsNumber || property.mls_number,
      price: property.price,
      // Handle both string address and object address formats
      address: typeof property.address === 'object' ? property.address.street : property.address,
      city: typeof property.address === 'object' ? property.address.city : property.city,
      state: typeof property.address === 'object' ? property.address.state : property.state,
      bedrooms: property.bedrooms,
      bathrooms: property.bathrooms,
      square_feet: property.sqft || property.square_feet,
      images: property.images || [],
      matchScore: property.matchScore,
      relevanceScore: property.relevanceScore,
      ranking: property.ranking,
      isTopMatch: property.isTopMatch,
      listing_brokerage: property.listingOffice || property.listing_brokerage,
      listing_agent: typeof property.listingAgent === 'object' ? property.listingAgent.name : property.listing_agent
    };
  };

  // Handle chat responses that include property searches
  const handleChatResponse = (response: any) => {
    if (response.properties && response.properties.length > 0) {
      // Transform properties to match PropertyCard expectations
      const transformedProperties = response.properties.map(transformProperty);
      setSearchResults(transformedProperties);
    }
  };

  const handlePropertyView = (propertyId: string) => {
    navigate(`/property/${propertyId}`);
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  return (
    <div className="property-chat-page">
      {/* Navigation handled by parent component */}

      <Container className="chat-container">
        <Row>
          <Col lg={4} className="chat-sidebar">
            <Card className="chat-card">
              <Card.Body>
                <h3>AI Property Assistant</h3>
                <p>Tell me what you're looking for and I'll help find the perfect home.</p>
                <MLSChatbot
                  initialMessage={state?.initialQuery}
                  onResponse={handleChatResponse}
                  onSearchStart={() => setIsSearching(true)}
                  onSearchComplete={() => setIsSearching(false)}
                />
              </Card.Body>
            </Card>
          </Col>
          
          <Col lg={8} className="results-section">
            {searchResults.length > 0 ? (
              <>
                <h3 className="results-title">
                  Found {searchResults.length} properties matching your criteria
                </h3>
                <Row>
                  {searchResults.map((property) => (
                    <Col md={6} key={property.id} className="mb-4">
                      <PropertyCard
                        property={property}
                        onView={() => handlePropertyView(property.id)}
                      />
                    </Col>
                  ))}
                </Row>
              </>
            ) : (
              <Card className="welcome-card">
                <Card.Body>
                  <h3>Start Your Property Search</h3>
                  <p>Chat with our AI to find homes that match your needs. Try asking:</p>
                  <ul>
                    <li>"Show me 3-bedroom homes in Tempe under $500k"</li>
                    <li>"I need a family home near good schools"</li>
                    <li>"Find investment properties with rental potential"</li>
                    <li>"What homes are available with a pool?"</li>
                  </ul>
                </Card.Body>
              </Card>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PropertyChat;